import type { BlockId, ChunkPos } from "../../sim/world/types";

export const PADDED_CHUNK_SIZE = 34;
export const PADDED_CHUNK_VOLUME = PADDED_CHUNK_SIZE ** 3;

export const RENDER_GROUPS = ["opaque", "cutout", "transparent", "water"] as const;
export type RenderGroup = (typeof RENDER_GROUPS)[number];

export const enum FaceDirection {
  PositiveX = 0,
  NegativeX = 1,
  PositiveY = 2,
  NegativeY = 3,
  PositiveZ = 4,
  NegativeZ = 5,
}

export interface BlockMeshDef {
  readonly id: BlockId;
  readonly opaque: boolean;
  readonly group: RenderGroup;
  /**
   * Base sRGB 0-255. The renderer has no textures: a voxel's albedo is this
   * colour, shaded per 2x2x2 cell (see materials/palette.ts).
   */
  readonly color: readonly [number, number, number];
  /** Peak-to-peak brightness spread across the shades, as a fraction of base. */
  readonly colorJitter: number;
  /** Shade per column rather than per 2x2x2 cell; see materials/palette.ts. */
  readonly columnShade: boolean;
  /** Bends in the wind; the vertex shader displaces it. */
  readonly sway: boolean;
}

export interface MeshingTables {
  /** 1 when a block fully occludes an adjacent face. Indexed by BlockId. */
  readonly opaque: Uint8Array;
  /** Index into RENDER_GROUPS. Indexed by BlockId. */
  readonly group: Uint8Array;
  /** Three sRGB channels per BlockId. */
  readonly color: Uint8Array;
  /** Jitter fraction per BlockId, quantised to 1/255. */
  readonly colorJitter: Uint8Array;
  /** 1 when the block shades per column. Indexed by BlockId. */
  readonly columnShade: Uint8Array;
  /** 1 when the block bends in the wind. Indexed by BlockId. */
  readonly sway: Uint8Array;
}

export interface MeshingInput {
  readonly position: ChunkPos;
  /** A 34³ dense neighborhood: the target chunk plus a one-block border. */
  readonly blocks: Uint16Array;
  readonly light: Uint16Array;
  readonly tables: MeshingTables;
}

export interface PackedMeshGroup {
  /** Packed position/normal/AO/corner words, one uint32 per vertex. */
  readonly attr0: ArrayBuffer;
  /** Packed light words, one uint32 per vertex. */
  readonly attr1: ArrayBuffer;
  /** Packed sRGB albedo, one uint32 per vertex. */
  readonly attr2: ArrayBuffer;
  readonly indices: ArrayBuffer;
  readonly vertexCount: number;
  readonly indexCount: number;
}

export interface MeshingResult {
  readonly position: ChunkPos;
  readonly groups: readonly [PackedMeshGroup, PackedMeshGroup, PackedMeshGroup, PackedMeshGroup];
  /** For each entry face, a bit mask of reachable exit faces through air. */
  readonly connectivity: Uint8Array;
}

export function paddedIndex(x: number, y: number, z: number): number {
  return x + PADDED_CHUNK_SIZE * (z + PADDED_CHUNK_SIZE * y);
}

export function createMeshingTables(definitions: readonly BlockMeshDef[]): MeshingTables {
  let maxId = 0;
  for (const definition of definitions) maxId = Math.max(maxId, definition.id);
  const opaque = new Uint8Array(maxId + 1);
  const group = new Uint8Array(maxId + 1);
  const color = new Uint8Array((maxId + 1) * 3);
  const colorJitter = new Uint8Array(maxId + 1);
  const columnShade = new Uint8Array(maxId + 1);
  const sway = new Uint8Array(maxId + 1);
  for (const definition of definitions) {
    opaque[definition.id] = definition.opaque ? 1 : 0;
    group[definition.id] = Math.max(0, RENDER_GROUPS.indexOf(definition.group));
    color[definition.id * 3] = definition.color[0] ?? 0;
    color[definition.id * 3 + 1] = definition.color[1] ?? 0;
    color[definition.id * 3 + 2] = definition.color[2] ?? 0;
    colorJitter[definition.id] = Math.round(Math.max(0, Math.min(1, definition.colorJitter)) * 255);
    columnShade[definition.id] = definition.columnShade ? 1 : 0;
    sway[definition.id] = definition.sway ? 1 : 0;
  }
  return { opaque, group, color, colorJitter, columnShade, sway };
}
